import { useState, useEffect, useCallback, useRef } from 'react';

export type ConnectionStatus = 'online' | 'offline' | 'checking' | 'uncertain';

interface OnlineDetectionResult {
  status: ConnectionStatus;
  isOnline: boolean;
  lastChecked: Date | null;
  consecutiveSuccesses: number;
  consecutiveFailures: number;
  performHealthCheck: () => Promise<void>;
}

const HEALTH_CHECK_URL = 'https://wxgazidevakcnkrngykk.supabase.co/rest/v1/';
const HEALTH_CHECK_TIMEOUT = 5000;
const AUTO_CHECK_INTERVAL = 120000; // 2 minutes (reduced from 30s)
const DEBOUNCE_DELAY = 1000;

export const useRobustOnlineDetection = (): OnlineDetectionResult => {
  const [status, setStatus] = useState<ConnectionStatus>('checking');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [consecutiveSuccesses, setConsecutiveSuccesses] = useState(0);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);

  const performHealthCheck = useCallback(async () => {
    if (isCheckingRef.current) return;
    
    isCheckingRef.current = true;
    setStatus('checking');
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);
      
      const response = await fetch(HEALTH_CHECK_URL, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        setConsecutiveSuccesses(prev => prev + 1);
        setConsecutiveFailures(0);
        setStatus('online');
        setLastChecked(new Date());
      } else {
        throw new Error('Health check failed');
      }
    } catch (error) {
      setConsecutiveFailures(prev => prev + 1);
      setConsecutiveSuccesses(0);
      setStatus('offline');
      setLastChecked(new Date());
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  const debouncedHealthCheck = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      performHealthCheck();
    }, DEBOUNCE_DELAY);
  }, [performHealthCheck]);

  // Initial health check on mount
  useEffect(() => {
    performHealthCheck();
  }, [performHealthCheck]);

  // Listen to browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('Browser reports: online');
      debouncedHealthCheck();
    };

    const handleOffline = () => {
      console.log('Browser reports: offline');
      // Don't trust browser immediately, verify with health check
      setStatus('uncertain');
      debouncedHealthCheck();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [debouncedHealthCheck]);

  // Auto-check interval when status is uncertain or offline
  useEffect(() => {
    if (status === 'uncertain' || status === 'offline') {
      autoCheckTimerRef.current = setInterval(() => {
        performHealthCheck();
      }, AUTO_CHECK_INTERVAL);
    } else {
      if (autoCheckTimerRef.current) {
        clearInterval(autoCheckTimerRef.current);
        autoCheckTimerRef.current = null;
      }
    }

    return () => {
      if (autoCheckTimerRef.current) {
        clearInterval(autoCheckTimerRef.current);
      }
    };
  }, [status, performHealthCheck]);

  // Monitor request patterns via window event
  useEffect(() => {
    const handleRequestSuccess = () => {
      setConsecutiveSuccesses(prev => {
        const newCount = prev + 1;
        if (newCount >= 3 && status !== 'online') {
          setStatus('online');
          setLastChecked(new Date());
        }
        return newCount;
      });
      setConsecutiveFailures(0);
    };

    const handleRequestFailure = () => {
      setConsecutiveFailures(prev => {
        const newCount = prev + 1;
        if (newCount >= 3 && status !== 'offline') {
          performHealthCheck(); // Verify with health check
        }
        return newCount;
      });
      setConsecutiveSuccesses(0);
    };

    window.addEventListener('network-request-success', handleRequestSuccess);
    window.addEventListener('network-request-failure', handleRequestFailure);

    return () => {
      window.removeEventListener('network-request-success', handleRequestSuccess);
      window.removeEventListener('network-request-failure', handleRequestFailure);
    };
  }, [status, performHealthCheck]);

  return {
    status,
    isOnline: status === 'online',
    lastChecked,
    consecutiveSuccesses,
    consecutiveFailures,
    performHealthCheck,
  };
};
